import DOMElement from "../../../dom-element";
import LocalizationsHelper from "../../../../../lib/localizations-helper";

export default class SelectElement extends DOMElement {
    constructor(props) {
        super('div', props);
    }

    _setProps() {
        super._setProps();

        this.id = this.props.id;
        this.layout = this.props.layout || 'single';

        if (!this.id) {
            throw new Error('SelectElement: ID is not defined.');
        }

        this.name = this.props.name;

        if (!this.name) {
            throw new Error('SelectElement: Name is not defined.');
        }

        if (this.layout === 'double' && !this.props.secondName) {
            throw new Error('SelectElement: Second name is not defined.');
        }

        this.containerId = `container-${this.id}`;
        this.listboxBtnId = `listbox-btn-${this.id}`;
        this.secondName = this.props.secondName;
        this.placeholder = this.props.placeholder;
        this.disabled = this.props.disabled;
        this.emptyText = this.props.emptyText || LocalizationsHelper.get('form.label.select');
        this.options = this.props.options || [];
        this.groups = this.props.groups || [];
        this.withSearch = this.props.withSearch != null ? this.props.withSearch : false;
    }

    _initProps() {
        super._initProps();

        Object.assign(this.attributes, {
            id: this.containerId,
            class: DOMElement.classListFrom('listbox', this.layout === 'double' ? '-double' : undefined, this.class)
        });

        this.children = [
            this._getButton(),
            new DOMElement('div', {
                class: 'listbox_container',
                attributes: {
                    'data-for': this.containerId
                },
                children: [
                    ...(this.withSearch ? [
                        this._getSearchInput(),
                        new DOMElement('p', {
                            class: 'listbox_not-found -hidden',
                            text: LocalizationsHelper.get('form.label.notFound')
                        })
                    ] : []),
                    new DOMElement('ul', {
                        class: 'listbox_opt-list',
                        attributes: {
                            tabindex: '-1',
                            role: 'listbox'
                        },
                        children: this.groups.length ? this._getGroupedOptions() : this._getOptions()
                    }),
                    ...(this.layout === 'double' ? [
                        new DOMElement('input', {
                            class: 'f-text-input',
                            attributes: {
                                type: 'text',
                                name: this.secondName,
                                id: this.id,
                                autocomplete: 'off',
                                placeholder: this.placeholder,
                            }
                        }),
                        new DOMElement('input', {
                            class: 'listbox_input',
                            attributes: {
                                type: 'hidden',
                                name: this.name,
                                autocomplete: 'off',
                                tabindex: '-1'
                            }
                        })
                    ] : [new DOMElement('input', {
                        class: 'listbox_input',
                        attributes: {
                            type: 'hidden',
                            name: this.name,
                            id: this.id,
                            autocomplete: 'off',
                            tabindex: '-1'
                        }
                    })])
                ]
            })
        ];
    }

    _getButton() {
        const buttonAttributes = {
            id: this.listboxBtnId,
            type: 'button',
            'data-for': this.containerId
        };

        if (this.disabled === true) {
            buttonAttributes.disabled = true;
        }

        return new DOMElement('button', {
            class: 'button f-select-button',
            attributes: buttonAttributes,
            children: [
                new DOMElement('span', {
                    class: 'button-content',
                    text: this.emptyText
                }),
                new DOMElement('template', {
                    class: 'icon-btn',
                    html: `<svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2L5.5 6 1 2" stroke="#0795E2" stroke-width="2"/></svg>`
                })
            ]
        });
    }

    _getSearchInput() {
        return new DOMElement('div', {
            class: 'listbox_search',
            children: [
                new DOMElement('input', {
                    class: 'listbox_search-input',
                    attributes: {
                        type: 'text',
                        placeholder: LocalizationsHelper.get('form.placeholder.search'),
                    }
                }),
                new DOMElement('span', {
                    class: 'listbox_search-icon -search',
                    html: '<svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="21.271" cy="21.245" r="19.005" stroke="#fff" stroke-width="3"/><path d="M34.869 34.627l17.366 17.365" stroke="#fff" stroke-width="3"/></svg>'
                }),
                new DOMElement('button', {
                    class: 'listbox_search-icon -cancel listbox_search-cancel',
                    attributes: {
                        type: 'button'
                    },
                    html: '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1l12 12m0-12L1 13" stroke="#E33C39" stroke-width="2"/></svg>'
                })
            ]
        });
    }

    _getGroupedOptions() {
        const options = [];

        for (const group of this.groups) {
            options.push(new DOMElement('li', {
                class: DOMElement.classListFrom('listbox_group', index === 0 ? '-first' : undefined),
                text: group.label
            }));

            if (!group.options || !Array.isArray(group.options) || !group.options.length) {
                continue;
            }

            for (const option of group.options) {
                options.push(new DOMElement('li', {
                    class: 'listbox_option',
                    attributes: {
                        'data-value': option.value,
                        role: 'option'
                    },
                    children: [
                        ...(option.icon ? [new DOMElement('img', {
                            class: 'listbox_icon lozad',
                            attributes: {
                                'data-src': option.icon,
                                alt: option.value
                            }
                        })] : []),
                        new DOMElement('span', {
                            class: 'listbox_text',
                            text: option.label
                        })
                    ]
                }));
            }
        }

        return options;
    }

    _getOptions() {
        return this.options.map((option) => new DOMElement('li', {
            class: 'listbox_option',
            attributes: {
                'data-value': option.value,
                role: 'option'
            },
            children: [
                ...(option.icon ? [new DOMElement('img', {
                    class: 'listbox_icon lozad',
                    attributes: {
                        'data-src': option.icon,
                        alt: option.value
                    }
                })] : []),
                new DOMElement('span', {
                    class: 'listbox_text',
                    text: option.label
                })
            ]
        }));
    }
}